import React, { useContext, useState, useEffect, createContext } from 'react'
import { User } from 'firebase/auth'
import { authStateListener } from '@config/auth/services/authService'

interface AuthContextType {
  userLoggedIn: boolean
  isEmailUser: boolean
  isGoogleUser: boolean
  currentUser: User | null
  setCurrentUser: React.Dispatch<React.SetStateAction<User | null>>
  jwtToken: string | null
  refreshToken: () => Promise<void>
}

const AuthContext = createContext<AuthContextType>({
  userLoggedIn: false,
  isEmailUser: false,
  isGoogleUser: false,
  currentUser: null,
  setCurrentUser: () => null,
  jwtToken: null,
  refreshToken: async () => {}
})

export function useAuth() {
  return useContext(AuthContext)
}

interface AuthProviderProps {
  children: React.ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [currentUser, setCurrentUser] = useState<User | null>(null)
  const [userLoggedIn, setUserLoggedIn] = useState(false)
  const [isEmailUser, setIsEmailUser] = useState(false)
  const [jwtToken, setJwtToken] = useState<string | null>(null)
  const isGoogleUser = false
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const unsubscribe = authStateListener(initializeUser)
    return unsubscribe
  }, [])

  async function initializeUser(user: User | null) {
    if (user) {
      setCurrentUser({ ...user })

      // Get the JWT token
      try {
        const token = await user.getIdToken()
        setJwtToken(token)
      } catch (error) {
        console.error('Error getting token:', error)
        setJwtToken(null)
      }

      // check if provider is email and password login
      const isEmail = user.providerData.some(
        provider => provider.providerId === 'password'
      )
      setIsEmailUser(isEmail)

      setUserLoggedIn(true)
    } else {
      setCurrentUser(null)
      setJwtToken(null)
      setUserLoggedIn(false)
    }

    setLoading(false)
  }

  // Function to manually refresh the token
  const refreshToken = async () => {
    if (currentUser) {
      try {
        const token = await currentUser.getIdToken(true) // Force refresh
        setJwtToken(token)
      } catch (error) {
        console.error('Error refreshing token:', error)
      }
    }
  }

  const value = React.useMemo(
    () => ({
      userLoggedIn,
      isEmailUser,
      isGoogleUser,
      currentUser,
      setCurrentUser,
      jwtToken,
      refreshToken
    }),
    [userLoggedIn, isEmailUser, isGoogleUser, currentUser, jwtToken]
  )

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  )
}
